import { useApolloClient } from "@apollo/client";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { assert, assertNever, findOrThrow } from "@msys/common";
import { ellipsisStyle, RadioGroup } from "@msys/ui";
import { Box, Button, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useField } from "formik";
import { isString, omit } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { AddressWithStreetNumberInput } from "../../../clients/graphqlTypes.js";
import {
  ActionLine,
  ContactLine,
  EmailLine,
  LocationLine,
  PhoneLine,
} from "../../commons/DataItem.js";
import { AutocompleteField } from "../../commons/form-fields/AutocompleteField.js";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";
import { EmailField } from "../../commons/form-fields/EmailField.js";
import { PhoneField } from "../../commons/form-fields/PhoneField.js";
import { SelectField } from "../../commons/form-fields/SelectField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { FormikClearErrorsOnChange } from "../../commons/FormikClearErrorsOnChange.js";
import {
  FormikObserveState,
  FormikStateObserver,
} from "../../commons/FormikStateObserver.js";
import {
  getAddressInfoFromPlace,
  getLocationFromPlace,
  GoogleAddressSearch,
  PlaceWithAddressComponentsAndLocation,
} from "../../commons/GoogleAddressSearch.js";
import { useCountries } from "../../commons/useCountries.js";
import { useGoogleMapsApi } from "../../google-maps/useGoogleMapsApi.js";
import { getAddressLabel, isPostalCodeValid } from "../addresses/helpers.js";
import { CreateCrmCompany_CrmCompanyFragment } from "../crm-companies/CrmCompany.generated.js";
import { CrmCompanySelect } from "../crm-companies/CrmCompanySelect.js";
import { useEmailAddressValidationSchema } from "../email/validateEmailAddress.js";
import { UserTitleWithNameField } from "../users/UserTitleWithNameField.js";
import {
  SapDuplicateCrmCompanyFragment,
  useCrmCreateModal__SapDataQuery,
  useSapCreateBusinessPartnerCraftsmanMutation,
  useSapCreateBusinessPartnerEndClientMutation,
  useSapSearchBusinessPartnerCustomerDuplicatesLazyQuery,
  useSapSearchBusinessPartnerEndClientDuplicatesLazyQuery,
} from "./S4HanaCrmCreateForm.generated.js";

interface Props {
  handleClose: () => void;
  handleComplete?: (
    handleClose: () => void,
    organisation: CreateCrmCompany_CrmCompanyFragment,
    userId?: string
  ) => Promise<void> | void;
  refetchQueries?: string[];
  prefill?: {
    email?: string;
  };
  formId: string;
  handleFormikObserveStateChange: (state: FormikObserveState) => void;
  isSubmitting?: boolean;
}

type SapS4HanaClientType = "END_CLIENT" | "CRAFTSMAN";
const defaultSapS4HanaClientType: SapS4HanaClientType = "END_CLIENT";

export const Sap4HanaCrmCreateForm = ({
  handleClose,
  handleComplete,
  refetchQueries,
  prefill,
  formId,
  handleFormikObserveStateChange,
  isSubmitting,
}: Props) => {
  const { t } = useTranslate(["Global", "CrmOrganisations", "SapS4Hana"]);
  const clientTypeOptions: { value: SapS4HanaClientType; label: string }[] = [
    { value: "END_CLIENT", label: t("End client", { ns: "SapS4Hana" }) },
    { value: "CRAFTSMAN", label: t("Craftsman", { ns: "SapS4Hana" }) },
  ];

  const [clientType, setClientType] = React.useState<SapS4HanaClientType>(
    defaultSapS4HanaClientType
  );

  const client = useApolloClient();

  const sapData = useCrmCreateModal__SapDataQuery({ client });

  const salutations = sapData.data?.sapValueHelp?.salutations;
  const salesOffices = sapData.data?.sapValueHelp?.businessPartnerSalesOffices;
  const crmUserIdCustomFieldKey =
    sapData.data?.sapConfig?.crmUserIdCustomFieldKey;
  const companyCodeOptions = sapData.data?.sapValueHelp?.companyCodes;
  const paymentTermsOptions = sapData.data?.sapValueHelp?.paymentTerms;
  const cashPlanningGroupOptions =
    sapData.data?.sapValueHelp?.cashPlanningGroup;
  const disableCraftsmenAccountingFields =
    sapData.data?.sapConfig?.disableCraftsmenAccountingFields ?? false;
  const craftsmenAccountingFieldsEnabled = !disableCraftsmenAccountingFields;
  const defaultCashPlanningGroupOption =
    cashPlanningGroupOptions?.find(
      o => o.value === sapData.data?.sapConfig?.defaultCashPlanningGroup
    ) ?? null;

  return (
    <Stack spacing={2} flex={1}>
      <RadioGroup<SapS4HanaClientType>
        inline
        options={clientTypeOptions}
        value={clientType}
        onChange={value => setClientType(value)}
        disabled={isSubmitting}
      />
      {clientType === "END_CLIENT" && crmUserIdCustomFieldKey && (
        <AddSapS4HanaEndClientForm
          formId={formId}
          handleComplete={async params => {
            switch (params.type) {
              case "SAP_CREATION": {
                const launchpadUrl = params.fioriLaunchPadUrl;
                if (Capacitor.isNativePlatform()) {
                  try {
                    await Browser.open({
                      url: launchpadUrl,
                      presentationStyle: "fullscreen",
                    });
                  } catch (e) {
                    const tab = window.open(launchpadUrl, "_system");
                    if (tab) tab.focus();
                  }
                } else {
                  const tab = window.open(launchpadUrl, "_system");
                  if (tab) tab.focus();
                }
                handleClose();
                break;
              }
              case "EXISTING_CRM": {
                if (handleComplete) {
                  handleComplete(handleClose, params.crmCompany);
                } else {
                  handleClose();
                }

                break;
              }
              default:
                assertNever(params);
            }
          }}
          refetchQueries={refetchQueries}
          handleFormikObserveStateChange={handleFormikObserveStateChange}
          prefill={prefill}
          salutations={salutations}
          salesOffices={salesOffices ?? []}
          companyCodeOptions={companyCodeOptions ?? []}
          paymentTermsOptions={paymentTermsOptions ?? []}
          cashPlanningGroupOptions={cashPlanningGroupOptions ?? []}
          defaults={{ cashPlanningGroup: defaultCashPlanningGroupOption }}
          crmUserIdCustomFieldKey={crmUserIdCustomFieldKey}
        />
      )}
      {clientType === "CRAFTSMAN" && (
        <AddSapS4HanaCraftsmanForm
          formId={formId}
          handleComplete={async params => {
            switch (params.type) {
              case "SAP_CREATION": {
                handleClose();
                break;
              }
              case "EXISTING_CRM": {
                if (handleComplete) {
                  handleComplete(handleClose, params.crmCompany);
                } else {
                  handleClose();
                }

                break;
              }
              default:
                assertNever(params);
            }
          }}
          refetchQueries={refetchQueries}
          handleFormikObserveStateChange={handleFormikObserveStateChange}
          prefill={prefill}
          salutations={salutations}
          salesOffices={salesOffices ?? []}
          accountingFieldsEnabled={
            craftsmenAccountingFieldsEnabled && !!crmUserIdCustomFieldKey
          }
          companyCodeOptions={companyCodeOptions ?? []}
          paymentTermsOptions={paymentTermsOptions ?? []}
          cashPlanningGroupOptions={cashPlanningGroupOptions ?? []}
          defaults={{ cashPlanningGroup: defaultCashPlanningGroupOption }}
          crmUserIdCustomFieldKey={crmUserIdCustomFieldKey}
        />
      )}
    </Stack>
  );
};

// Add SAP end client

interface AddSapS4HanaEndClientFormValues {
  salutation: string;
  firstname: string;
  familyname: string;
  phone: string;
  email: string;
  address: AddressWithStreetNumberInput | null;
  hasConsentToDataProcessing: boolean;
  duplicateCheckDone: boolean;
  salesOffice: { value: string; label: string } | null;
  salesRepresentative: { crmCompanyId: string; sapUserId: string } | null;
  companyCode: { value: string; label: string } | null;
  paymentTerms: { value: string; label: string } | null;
  cashPlanningGroup: { value: string; label: string } | null;
}

function AddSapS4HanaEndClientForm({
  formId,
  refetchQueries,
  handleComplete,
  handleFormikObserveStateChange,
  prefill,
  salutations,
  salesOffices,
  companyCodeOptions,
  paymentTermsOptions,
  cashPlanningGroupOptions,
  defaults,
  crmUserIdCustomFieldKey,
}: {
  formId: string;
  refetchQueries?: string[];
  handleComplete: (
    params:
      | {
          type: "EXISTING_CRM";
          crmCompany: CreateCrmCompany_CrmCompanyFragment;
        }
      | { type: "SAP_CREATION"; fioriLaunchPadUrl: string }
  ) => void;
  handleFormikObserveStateChange: (state: FormikObserveState) => void;
  // Caller-supplied data from the invoking context (e.g. email from a lead)
  prefill?: {
    email?: string;
  };
  // Organisation-wide defaults derived from integration settings
  defaults?: {
    cashPlanningGroup?: { value: string; label: string } | null;
  };
  salutations: { value: string; label: string }[] | undefined;
  salesOffices: { value: string; label: string }[];
  companyCodeOptions: { value: string; label: string }[];
  paymentTermsOptions: { value: string; label: string }[];
  cashPlanningGroupOptions: { value: string; label: string }[];
  crmUserIdCustomFieldKey: string;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate([
    "UserDetails",
    "OrganisationProfile",
    "Global",
    "SapS4Hana",
    "CrmOrganisations",
  ]);

  const { createEmailAddressValidationSchema } =
    useEmailAddressValidationSchema();

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape(
        {
          salutation: Yup.string()
            .label(t("Salutation", { ns: "Global" }))
            .required(),
          firstname: Yup.string()
            .label(t("First name", { ns: "UserDetails" }))
            .required()
            .min(1),
          familyname: Yup.string()
            .label(t("Last name", { ns: "UserDetails" }))
            .required()
            .min(1),
          phone: Yup.string()
            .label(t("Phone", { ns: "UserDetails" }))
            .required(),
          email: createEmailAddressValidationSchema(
            t("Email", { ns: "UserDetails" })
          ).required(),
          address: Yup.object()
            .shape({
              streetLine1: Yup.string(),
              postalCode: Yup.string().test(
                "validPostalCode",
                t("Invalid postal code", { ns: "Global" }),
                (postalCode, context) => {
                  const countryCode = context.parent.countryCode;
                  return postalCode
                    ? isPostalCodeValid({ postalCode, countryCode })
                    : false;
                }
              ),
              city: Yup.string(),
            })
            .label(t("Address", { ns: "CrmOrganisations" }))
            .nullable()
            .required(t("Address is mandatory", { ns: "Global" })),
          hasConsentToDataProcessing: Yup.boolean()
            .label(
              t("End client gave consent to data processing", {
                ns: "SapS4Hana",
              })
            )
            .isTrue(
              t("Client must gave consent to data processing", {
                ns: "SapS4Hana",
              })
            )
            .required(),
          duplicateCheckDone: Yup.boolean()
            .isTrue(
              t("Please check for duplicates first", {
                ns: "SapS4Hana",
              })
            )
            .required(),
          salesOffice: Yup.object({
            value: Yup.string().required(),
            label: Yup.string().required(),
          }).required(),
          salesRepresentative: Yup.object({
            crmCompanyId: Yup.string().required(),
            sapUserId: Yup.string().required(),
          }).required(),
          companyCode: Yup.object({
            value: Yup.string().required(),
            label: Yup.string().required(),
          }).required(),
          paymentTerms: Yup.object({
            value: Yup.string().required(),
            label: Yup.string().required(),
          }).required(),
          cashPlanningGroup: Yup.object({
            value: Yup.string().required(),
            label: Yup.string().required(),
          }).required(),
        },
        [
          ["salutation", "firstname"],
          ["salutation", "familyname"],
          ["firstname", "familyname"],
        ]
      ),
    [createEmailAddressValidationSchema, t]
  );

  const initialValues = React.useMemo(
    (): AddSapS4HanaEndClientFormValues => ({
      salutation: "mr",
      firstname: "",
      familyname: "",
      phone: "",
      email: prefill?.email ?? "",
      address: null,
      hasConsentToDataProcessing: false,
      duplicateCheckDone: false,
      salesOffice: null,
      salesRepresentative: null,
      companyCode: null,
      paymentTerms: null,
      cashPlanningGroup: defaults?.cashPlanningGroup ?? null,
    }),
    [prefill?.email, defaults?.cashPlanningGroup]
  );

  const client = useApolloClient();
  const [sapCreateBusinessPartnerEndClient] =
    useSapCreateBusinessPartnerEndClientMutation({
      client,
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [
    sapSearchBusinessPartnerEndClientDuplicates,
    {
      data: sapSearchBusinessPartnerEndClientDuplicatesData,
      loading: sapSearchBusinessPartnerEndClientDuplicatesLoading,
    },
  ] = useSapSearchBusinessPartnerEndClientDuplicatesLazyQuery({
    client,
  });
  const onSubmit = async (values: AddSapS4HanaEndClientFormValues) => {
    assert(values.address, "Address is required");
    assert(values.salesOffice, "Sales office is required");
    assert(values.salesRepresentative, "Sales representative is required");
    assert(values.companyCode, "Company code is required");
    assert(values.paymentTerms, "Payment terms are required");
    assert(values.cashPlanningGroup, "Cash planning group is required");

    try {
      const r = await sapCreateBusinessPartnerEndClient({
        variables: {
          input: {
            salutation: values.salutation,
            firstname: values.firstname,
            familyname: values.familyname,
            phone: values.phone,
            email: values.email,
            address: {
              ...omit(values.address, "__typename"),
              location: values.address.location
                ? omit(values.address.location, "__typename")
                : null,
            },
            salesOffice: values.salesOffice.value,
            salesRepresentativeUserId: JSON.parse(
              values.salesRepresentative.sapUserId
            ),
            companyCode: values.companyCode.value,
            paymentTerms: values.paymentTerms.value,
            cashPlanningGroup: values.cashPlanningGroup.value,
          },
        },
      });
      enqueueSnackbar(t("Client created", { ns: "UserDetails" }));
      if (r.data?.sapCreateBusinessPartnerEndClient.fioriLaunchPadUrl) {
        handleComplete({
          type: "SAP_CREATION",
          fioriLaunchPadUrl:
            r.data.sapCreateBusinessPartnerEndClient.fioriLaunchPadUrl,
        });
      } else {
        throw new Error(`No url provided in response`);
      }
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  return (
    <Formik<AddSapS4HanaEndClientFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {formikProps => (
        <Form id={formId}>
          <Stack spacing={1}>
            <Box>
              <UserTitleWithNameField
                titleField="salutation"
                titleOptions={salutations}
                firstnameField="firstname"
                familynameField="familyname"
                isRequiredOption="BOTH"
              />
            </Box>
            <PhoneField
              label={t("Phone", { ns: "UserDetails" })}
              name="phone"
              required
            />
            <EmailField
              label={t("Email", { ns: "UserDetails" })}
              name="email"
              required
            />
            <AddressField />
            <AutocompleteField
              inputLabel={t("Sales office", { ns: "SapS4Hana" })}
              name={"salesOffice"}
              disabled={formikProps.isSubmitting}
              options={salesOffices}
              getOptionLabel={option => {
                return `${option.value} - ${option.label}`;
              }}
              isRequired={true}
            />
            <CrmCompanySelect
              crmCompanyId={
                formikProps.values.salesRepresentative?.crmCompanyId
              }
              inputLabel={t("Sales representative", { ns: "SapS4Hana" })}
              required
              onChange={crmCompany => {
                formikProps.setValues(values => ({
                  ...values,
                  salesRepresentative: crmCompany
                    ? {
                        crmCompanyId: crmCompany.id,
                        sapUserId: findOrThrow(
                          crmCompany.customFields,
                          cf => cf.key === crmUserIdCustomFieldKey
                        ).value,
                      }
                    : null,
                }));
              }}
              canCreateNew={false}
              disabled={formikProps.isSubmitting}
              filterCustomFields={[
                { key: crmUserIdCustomFieldKey, exist: true },
              ]}
            />
            <AutocompleteField
              inputLabel={t("Company code", { ns: "SapS4Hana" })}
              name={"companyCode"}
              disabled={formikProps.isSubmitting}
              options={companyCodeOptions}
              getOptionLabel={option => {
                return `${option.value} - ${option.label}`;
              }}
              isRequired={true}
            />
            <AutocompleteField
              inputLabel={t("Cash planning group", { ns: "SapS4Hana" })}
              name={"cashPlanningGroup"}
              disabled={formikProps.isSubmitting}
              options={cashPlanningGroupOptions}
              getOptionLabel={option => {
                return `${option.value} - ${option.label}`;
              }}
              isRequired={true}
            />
            <AutocompleteField
              inputLabel={t("Payment terms", { ns: "SapS4Hana" })}
              name={"paymentTerms"}
              disabled={formikProps.isSubmitting}
              options={paymentTermsOptions}
              getOptionLabel={option => {
                return `${option.value} - ${option.label}`;
              }}
              isRequired={true}
            />
            <CheckboxField
              name={"hasConsentToDataProcessing"}
              label={t("End client gave consent to data processing", {
                ns: "SapS4Hana",
              })}
            />
            <Button
              size="small"
              color="secondary"
              variant={"contained"}
              style={{ maxWidth: "200px" }}
              disabled={
                !(
                  formikProps.values.firstname &&
                  formikProps.values.familyname &&
                  formikProps.values.address
                ) || sapSearchBusinessPartnerEndClientDuplicatesLoading
              }
              onClick={async e => {
                e.preventDefault();
                e.stopPropagation();

                assert(formikProps.values.address, "Address is required");

                await sapSearchBusinessPartnerEndClientDuplicates({
                  variables: {
                    input: {
                      firstname: formikProps.values.firstname,
                      familyname: formikProps.values.familyname,
                      address: {
                        countryCode: formikProps.values.address.countryCode,
                        postalCode: formikProps.values.address.postalCode,
                        city: formikProps.values.address.city,
                        streetLines1:
                          `${formikProps.values.address.streetName ?? ""} ${formikProps.values.address.streetNumber ?? ""}`.trim(),
                        location: formikProps.values.address.location,
                      },
                    },
                  },
                  onCompleted: () => {
                    formikProps.setFieldValue("duplicateCheckDone", true, true);
                  },
                });
              }}
            >
              {t("Check for duplicates", {
                ns: "SapS4Hana",
              })}
            </Button>
            {formikProps.errors.duplicateCheckDone &&
            isString(formikProps.errors.duplicateCheckDone) ? (
              <Typography variant="caption" color="error.main">
                {formikProps.errors.duplicateCheckDone}
              </Typography>
            ) : null}
            {sapSearchBusinessPartnerEndClientDuplicatesData &&
              (sapSearchBusinessPartnerEndClientDuplicatesData
                .sapSearchBusinessPartnerEndClientDuplicates.scoredCrmCompanies
                .length > 0 ? (
                <Stack>
                  {sapSearchBusinessPartnerEndClientDuplicatesData.sapSearchBusinessPartnerEndClientDuplicates.scoredCrmCompanies
                    .slice(0, 5)
                    .map(({ crmCompany }) => (
                      <SapCrmCompanyDuplicateCheckListItem
                        key={crmCompany.id}
                        crmCompany={crmCompany}
                        Action={
                          <Button
                            size="medium"
                            color="primary"
                            variant={"contained"}
                            onClick={async e => {
                              e.preventDefault();
                              e.stopPropagation();

                              handleComplete({
                                type: "EXISTING_CRM",
                                crmCompany,
                              });
                            }}
                          >
                            {t("Pick", {
                              ns: "SapS4Hana",
                            })}
                          </Button>
                        }
                      />
                    ))}
                </Stack>
              ) : (
                <Typography>
                  {t("No duplicates found", { ns: "SapS4Hana" })}
                </Typography>
              ))}
          </Stack>
          <FormikStateObserver callback={handleFormikObserveStateChange} />
          <FormikClearErrorsOnChange />
        </Form>
      )}
    </Formik>
  );
}

interface AddSapS4HanaCraftsmanFormValues {
  salutation: string;
  firstname: string;
  familyname: string;
  companyName: string;
  phone: string;
  email: string;
  address: AddressWithStreetNumberInput | null;
  isFastCreation: boolean;
  duplicateCheckDone: boolean;
  salesOffice: { value: string; label: string } | null;
  salesRepresentative: { crmCompanyId: string; sapUserId: string } | null;
  companyCode: { value: string; label: string } | null;
  paymentTerms: { value: string; label: string } | null;
  cashPlanningGroup: { value: string; label: string } | null;
}

function AddSapS4HanaCraftsmanForm({
  formId,
  refetchQueries,
  handleComplete,
  handleFormikObserveStateChange,
  prefill,
  salutations,
  salesOffices,
  accountingFieldsEnabled,
  companyCodeOptions,
  paymentTermsOptions,
  cashPlanningGroupOptions,
  defaults,
  crmUserIdCustomFieldKey,
}: {
  formId: string;
  refetchQueries?: string[];
  handleComplete: (
    params:
      | {
          type: "EXISTING_CRM";
          crmCompany: CreateCrmCompany_CrmCompanyFragment;
        }
      | { type: "SAP_CREATION" }
  ) => void;
  handleFormikObserveStateChange: (state: FormikObserveState) => void;
  // Caller-supplied data from the invoking context (e.g. email from a lead)
  prefill?: {
    email?: string;
  };
  // Organisation-wide defaults derived from integration settings
  defaults?: {
    cashPlanningGroup?: { value: string; label: string } | null;
  };
  salutations: { value: string; label: string }[] | undefined;
  salesOffices: { value: string; label: string }[];
  accountingFieldsEnabled: boolean;
  companyCodeOptions: { value: string; label: string }[];
  paymentTermsOptions: { value: string; label: string }[];
  cashPlanningGroupOptions: { value: string; label: string }[];
  crmUserIdCustomFieldKey: string | undefined;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate([
    "UserDetails",
    "OrganisationProfile",
    "Global",
    "SapS4Hana",
    "CrmOrganisations",
  ]);

  const { createEmailAddressValidationSchema } =
    useEmailAddressValidationSchema();

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape(
        {
          salutation: Yup.string()
            .label(t("Salutation", { ns: "Global" }))
            .required(),
          firstname: Yup.string()
            .label(t("First name", { ns: "UserDetails" }))
            .required()
            .min(1),
          familyname: Yup.string()
            .label(t("Last name", { ns: "UserDetails" }))
            .required()
            .min(1),
          companyName: Yup.string()
            .label(t("Company Name", { ns: "UserDetails" }))
            .required()
            .min(1),
          phone: Yup.string()
            .label(t("Phone", { ns: "UserDetails" }))
            .required(),
          email: createEmailAddressValidationSchema(
            t("Email", { ns: "UserDetails" })
          ).required(),
          address: Yup.object()
            .shape({
              streetLine1: Yup.string(),
              postalCode: Yup.string().test(
                "validPostalCode",
                t("Invalid postal code", { ns: "Global" }),
                (postalCode, context) => {
                  const countryCode = context.parent.countryCode;
                  return postalCode
                    ? isPostalCodeValid({ postalCode, countryCode })
                    : false;
                }
              ),
              city: Yup.string(),
            })
            .label(t("Address", { ns: "CrmOrganisations" }))
            .nullable()
            .required(t("Address is mandatory", { ns: "Global" })),
          duplicateCheckDone: Yup.boolean()
            .isTrue(
              t("Please check for duplicates first", {
                ns: "SapS4Hana",
              })
            )
            .required(),
          salesOffice: Yup.object({
            value: Yup.string().required(),
            label: Yup.string().required(),
          }).required(),
          ...(accountingFieldsEnabled
            ? {
                salesRepresentative: Yup.object({
                  crmCompanyId: Yup.string().required(),
                  sapUserId: Yup.string().required(),
                }).required(),
                companyCode: Yup.object({
                  value: Yup.string().required(),
                  label: Yup.string().required(),
                }).required(),
                paymentTerms: Yup.object({
                  value: Yup.string().required(),
                  label: Yup.string().required(),
                }).required(),
                cashPlanningGroup: Yup.object({
                  value: Yup.string().required(),
                  label: Yup.string().required(),
                }).required(),
              }
            : {}),
        },
        [
          ["salutation", "firstname"],
          ["salutation", "familyname"],
          ["firstname", "familyname"],
        ]
      ),
    [accountingFieldsEnabled, createEmailAddressValidationSchema, t]
  );

  const initialValues = React.useMemo(
    (): AddSapS4HanaCraftsmanFormValues => ({
      salutation: "mr",
      firstname: "",
      familyname: "",
      phone: "",
      email: prefill?.email ?? "",
      address: null,
      companyName: "",
      isFastCreation: false,
      duplicateCheckDone: false,
      salesOffice: null,
      salesRepresentative: null,
      companyCode: null,
      paymentTerms: null,
      cashPlanningGroup: defaults?.cashPlanningGroup ?? null,
    }),
    [prefill?.email, defaults?.cashPlanningGroup]
  );

  const client = useApolloClient();
  const [sapCreateBusinessPartnerCustomer] =
    useSapCreateBusinessPartnerCraftsmanMutation({
      client,
      refetchQueries,
      awaitRefetchQueries: true,
    });
  const [
    sapSearchBusinessPartnerCustomerDuplicates,
    {
      data: sapSearchBusinessPartnerCustomerDuplicatesData,
      loading: sapSearchBusinessPartnerCustomerDuplicatesLoading,
    },
  ] = useSapSearchBusinessPartnerCustomerDuplicatesLazyQuery({
    client,
  });
  const onSubmit = async (values: AddSapS4HanaCraftsmanFormValues) => {
    if (!values.address) {
      return;
    }

    if (!values.salesOffice) {
      return;
    }

    if (
      accountingFieldsEnabled &&
      (!values.salesRepresentative ||
        !values.companyCode ||
        !values.paymentTerms ||
        !values.cashPlanningGroup)
    ) {
      return;
    }

    try {
      await sapCreateBusinessPartnerCustomer({
        variables: {
          input: {
            salutation: values.salutation,
            firstname: values.firstname,
            familyname: values.familyname,
            phone: values.phone,
            email: values.email,
            address: {
              ...values.address,
              location: values.address.location ?? null,
            },
            companyName: values.companyName,
            isFastCreation: values.isFastCreation,
            salesOffice: values.salesOffice.value,
            ...(accountingFieldsEnabled &&
            values.companyCode &&
            values.cashPlanningGroup &&
            values.paymentTerms &&
            values.salesRepresentative
              ? {
                  companyCode: values.companyCode.value,
                  cashPlanningGroup: values.cashPlanningGroup.value,
                  paymentTerms: values.paymentTerms.value,
                  salesRepresentativeUserId: JSON.parse(
                    values.salesRepresentative.sapUserId
                  ),
                }
              : {}),
          },
        },
      });
      enqueueSnackbar(t("Craftsman created", { ns: "UserDetails" }));
      handleComplete({ type: "SAP_CREATION" });
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  return (
    <Formik<AddSapS4HanaCraftsmanFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {formikProps => (
        <Form id={formId}>
          <Stack spacing={1}>
            <TextField
              name="companyName"
              label={t("Company Name", {
                ns: "UserDetails",
              })}
              required
            />
            <Box>
              <UserTitleWithNameField
                titleField="salutation"
                titleOptions={salutations}
                firstnameField="firstname"
                familynameField="familyname"
                isRequiredOption="BOTH"
              />
            </Box>
            <PhoneField
              label={t("Phone", { ns: "UserDetails" })}
              name="phone"
              required
            />
            <EmailField
              label={t("Email", { ns: "UserDetails" })}
              name="email"
              required
            />
            <AddressField />
            <AutocompleteField
              inputLabel={t("Sales office", { ns: "SapS4Hana" })}
              name={"salesOffice"}
              disabled={formikProps.isSubmitting}
              options={salesOffices}
              getOptionLabel={option => {
                return `${option.value} - ${option.label}`;
              }}
              isRequired={true}
            />
            {accountingFieldsEnabled && crmUserIdCustomFieldKey && (
              <>
                <CrmCompanySelect
                  crmCompanyId={
                    formikProps.values.salesRepresentative?.crmCompanyId
                  }
                  inputLabel={t("Sales representative", { ns: "SapS4Hana" })}
                  required
                  onChange={crmCompany => {
                    formikProps.setValues(values => ({
                      ...values,
                      salesRepresentative: crmCompany
                        ? {
                            crmCompanyId: crmCompany.id,
                            sapUserId: findOrThrow(
                              crmCompany.customFields,
                              cf => cf.key === crmUserIdCustomFieldKey
                            ).value,
                          }
                        : null,
                    }));
                  }}
                  canCreateNew={false}
                  disabled={formikProps.isSubmitting}
                  filterCustomFields={[
                    { key: crmUserIdCustomFieldKey, exist: true },
                  ]}
                />
                <AutocompleteField
                  inputLabel={t("Company code", { ns: "SapS4Hana" })}
                  name={"companyCode"}
                  disabled={formikProps.isSubmitting}
                  options={companyCodeOptions}
                  getOptionLabel={option => {
                    return `${option.value} - ${option.label}`;
                  }}
                  isRequired={true}
                />
                <AutocompleteField
                  inputLabel={t("Cash planning group", { ns: "SapS4Hana" })}
                  name={"cashPlanningGroup"}
                  disabled={formikProps.isSubmitting}
                  options={cashPlanningGroupOptions}
                  getOptionLabel={option => {
                    return `${option.value} - ${option.label}`;
                  }}
                  isRequired={true}
                />
                <AutocompleteField
                  inputLabel={t("Payment terms", { ns: "SapS4Hana" })}
                  name={"paymentTerms"}
                  disabled={formikProps.isSubmitting}
                  options={paymentTermsOptions}
                  getOptionLabel={option => {
                    return `${option.value} - ${option.label}`;
                  }}
                  isRequired={true}
                />
              </>
            )}
            <CheckboxField
              name={"isFastCreation"}
              label={t("Do fast creation", {
                ns: "SapS4Hana",
              })}
            />
            <Button
              size="small"
              color="secondary"
              variant={"contained"}
              style={{ maxWidth: "200px" }}
              disabled={
                !(
                  formikProps.values.companyName &&
                  formikProps.values.firstname &&
                  formikProps.values.familyname &&
                  formikProps.values.address
                ) || sapSearchBusinessPartnerCustomerDuplicatesLoading
              }
              onClick={async e => {
                e.preventDefault();
                e.stopPropagation();

                assert(formikProps.values.address, "Address must be set");

                await sapSearchBusinessPartnerCustomerDuplicates({
                  variables: {
                    input: {
                      firstname: formikProps.values.firstname,
                      familyname: formikProps.values.familyname,
                      address: {
                        countryCode: formikProps.values.address.countryCode,
                        postalCode: formikProps.values.address.postalCode,
                        city: formikProps.values.address.city,
                        streetLines1: `${formikProps.values.address.streetName} ${formikProps.values.address.streetNumber}`,
                      },
                      companyName: formikProps.values.companyName,
                    },
                  },
                  onCompleted: () => {
                    formikProps.setFieldValue("duplicateCheckDone", true, true);
                  },
                });
              }}
            >
              {t("Check for duplicates", {
                ns: "SapS4Hana",
              })}
            </Button>
            {formikProps.errors.duplicateCheckDone &&
            isString(formikProps.errors.duplicateCheckDone) ? (
              <Typography variant="caption" color="error.main">
                {formikProps.errors.duplicateCheckDone}
              </Typography>
            ) : null}
            {sapSearchBusinessPartnerCustomerDuplicatesData &&
              (sapSearchBusinessPartnerCustomerDuplicatesData
                .sapSearchBusinessPartnerCustomerDuplicates.scoredCrmCompanies
                .length > 0 ? (
                <Stack>
                  {sapSearchBusinessPartnerCustomerDuplicatesData.sapSearchBusinessPartnerCustomerDuplicates.scoredCrmCompanies
                    .slice(0, 5)
                    .map(({ crmCompany }) => (
                      <SapCrmCompanyDuplicateCheckListItem
                        key={crmCompany.id}
                        crmCompany={crmCompany}
                        Action={
                          <Button
                            size="small"
                            color="primary"
                            variant={"contained"}
                            onClick={async e => {
                              e.preventDefault();
                              e.stopPropagation();

                              handleComplete({
                                type: "EXISTING_CRM",
                                crmCompany,
                              });
                            }}
                          >
                            {t("Pick", {
                              ns: "SapS4Hana",
                            })}
                          </Button>
                        }
                      />
                    ))}
                </Stack>
              ) : (
                <Typography>
                  {t("No duplicates found", { ns: "SapS4Hana" })}
                </Typography>
              ))}
          </Stack>
          <FormikStateObserver callback={handleFormikObserveStateChange} />
          <FormikClearErrorsOnChange />
        </Form>
      )}
    </Formik>
  );
}

function SapCrmCompanyDuplicateCheckListItem({
  crmCompany,
  Action,
}: {
  crmCompany: SapDuplicateCrmCompanyFragment;
  Action?: React.ReactElement;
}) {
  const { t } = useTranslate("DataItem");
  const contact = crmCompany.contactPerson?.fullname;

  const location = crmCompany.billingAddress
    ? getAddressLabel(crmCompany.billingAddress)
    : undefined;

  const phones =
    crmCompany.phones.length > 0
      ? crmCompany.phones.map(p => p.number).join(", ")
      : undefined;

  return (
    <Stack direction="column" spacing={0.5} minWidth={0}>
      <Stack
        direction="row"
        spacing={1}
        minWidth={0}
        justifyContent="space-between"
      >
        <Typography variant={"h4"}>
          <span>{`${crmCompany.number} - ${crmCompany.title}`}</span>
        </Typography>
        {Action ? <ActionLine>{Action}</ActionLine> : null}
      </Stack>
      {location ? (
        <Box
          gap={0.5}
          display="flex"
          flexDirection="column"
          flexWrap="wrap"
          style={ellipsisStyle}
        >
          <Tooltip title={t("Location")} arrow placement="bottom">
            <div style={{ ...ellipsisStyle, alignSelf: "flex-start" }}>
              <LocationLine>{location}</LocationLine>
            </div>
          </Tooltip>
        </Box>
      ) : null}
      {crmCompany.email ? (
        <Box
          gap={0.5}
          display="flex"
          flexDirection="column"
          flexWrap="wrap"
          style={ellipsisStyle}
        >
          <Tooltip title={t("Email")} arrow placement="bottom">
            <div style={{ ...ellipsisStyle, alignSelf: "flex-start" }}>
              <EmailLine>{crmCompany.email}</EmailLine>
            </div>
          </Tooltip>
        </Box>
      ) : null}
      {phones ? (
        <Box
          gap={0.5}
          display="flex"
          flexDirection="column"
          flexWrap="wrap"
          style={ellipsisStyle}
        >
          <Tooltip title={t("Email")} arrow placement="bottom">
            <div style={{ ...ellipsisStyle, alignSelf: "flex-start" }}>
              <PhoneLine>{phones}</PhoneLine>
            </div>
          </Tooltip>
        </Box>
      ) : null}
      {contact ? (
        <Box
          gap={0.5}
          display="flex"
          flexDirection="column"
          flexWrap="wrap"
          style={ellipsisStyle}
        >
          <Tooltip title={t("Contact")} arrow placement="bottom">
            <div style={{ ...ellipsisStyle, alignSelf: "flex-start" }}>
              <ContactLine>{contact}</ContactLine>
            </div>
          </Tooltip>
        </Box>
      ) : null}
    </Stack>
  );
}

const AddressField = () => {
  const { t } = useTranslate(["Address"]);
  const { isLoaded } = useGoogleMapsApi();
  const countries = useCountries();

  const [selectedGooglePlace, setSelectedGooglePlace] =
    React.useState<PlaceWithAddressComponentsAndLocation | null>(null);
  const [{ value }, _, { setValue }] =
    useField<AddressWithStreetNumberInput | null>("address");

  const { location, isValid } = React.useMemo(() => {
    const location = selectedGooglePlace
      ? getLocationFromPlace(selectedGooglePlace)
      : null;

    const addressInfo = selectedGooglePlace
      ? getAddressInfoFromPlace(selectedGooglePlace)
      : null;

    const isValid =
      value?.countryCode === addressInfo?.countryCode &&
      value?.city === addressInfo?.city &&
      value?.postalCode === addressInfo?.postalCode &&
      value?.streetName === addressInfo?.streetName &&
      value?.streetNumber === addressInfo?.streetNumber;

    return { location, isValid };
  }, [selectedGooglePlace, value]);

  const setInputValueRef = React.useRef<(inputValue: string) => void>();

  React.useEffect(() => {
    if (!isValid) {
      setInputValueRef.current?.("");
    }
  }, [isValid]);

  return (
    <Stack spacing={1}>
      <GoogleAddressSearch<never, never>
        label={t("Address search", { ns: "Address" })}
        onChange={address => {
          if (!address) {
            setValue(null);
          } else {
            const { place } = address;

            setSelectedGooglePlace(place);
            const addressInfo = getAddressInfoFromPlace(place);

            setValue({
              streetName: addressInfo.streetName ?? "",
              streetNumber: addressInfo.streetNumber ?? "",
              postalCode: addressInfo.postalCode ?? "",
              city: addressInfo.city ?? "",
              countryCode: addressInfo.countryCode ?? "",
            });
          }
        }}
        setInputValueRef={setInputValueRef}
      />
      {value && (
        <>
          {isLoaded && location && (
            <div style={{ position: "relative" }}>
              <GoogleMap
                options={{
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                  cameraControl: false,
                  zoomControl: false,
                  disableDoubleClickZoom: true,
                  draggable: false,
                  scrollwheel: false,
                }}
                mapTypeId={google.maps.MapTypeId.ROADMAP}
                mapContainerStyle={{
                  width: "100%",
                  aspectRatio: "16/5",
                }}
                zoom={15}
                center={location}
              >
                <MarkerF visible position={location} />
              </GoogleMap>
              {!isValid && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    color: "secondary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1rem",
                    textAlign: "center",
                  }}
                >
                  {t(
                    "Address was changed manually and location does not reflect current address any more",
                    { ns: "Address" }
                  )}
                </Box>
              )}
            </div>
          )}
          <Grid container columns={8} spacing={1}>
            <Grid size={{ xs: 6 }}>
              <TextField
                name="address.streetName"
                label={t("Street name", { ns: "Address" })}
                required
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <TextField
                name="address.streetNumber"
                label={t("Street number", { ns: "Address" })}
                required
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <TextField
                name="address.postalCode"
                label={t("Post code", { ns: "Address" })}
                required
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField
                name="address.city"
                label={t("City/town", { ns: "Address" })}
                required
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <SelectField
                name="address.countryCode"
                label={t("Country", { ns: "Address" })}
                required
                options={countries.map(country => ({
                  value: country.code,
                  label: country.name,
                }))}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
};
